import { Component, DestroyRef, Input, OnChanges, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AccessOptions } from '@shared/models/accessOptions.model';
import { HospitalNames } from '@shared/models/hospitalTypes.model';
import { InstanceIdService } from '@shared/services/instance-id.service';
import { SubscriptionManager } from '@shared/services/subscription-manager.service';
import moment from 'moment';
import { Observable, forkJoin } from 'rxjs';
import { ANESTHETIST, BLOC, CONSULTATION, IADE } from 'src/app/shared/const/globals.const';
import { Formation } from '../../models/formation.model';
import { Paramedical } from '../../models/paramedical.model';
import { CalendarRequestService } from '../../services/calendar-request.service';
import { ParamedicalService } from '../../services/paramedical.service';
import { getFirstHospitalSelectedData, sortByDate } from '../../utils/cross-functions';
import { NURSE } from './../../const/globals.const';
import { ErrorService } from './../../services/error.service';
import { ProfileService } from './../../services/profile.service';
import { UserService } from './../../services/user.service';

@Component({
  selector: 'app-popup-availalability-request-infos',
  templateUrl: './popup-availalability-request-infos.component.html',
  styleUrls: ['./popup-availalability-request-infos.component.scss'],
  standalone: false,
})
export class PopupAvailalabilityRequestInfosComponent implements OnInit, OnChanges {
  private instanceId: string;

  @Input() public cr: any;

  public isCRCampaign: boolean = false;

  public requestsCounter: any;

  public effectif: any;

  public originalEffective: any;

  public originalNeeds: any;

  private needs: any;

  private dayDiffs: number[] = [];

  public needsEffectifs: { effectifs: any; needs: any; difference: number }[] | undefined;

  private position: string | undefined;

  public isAnesthesiste: boolean = false;

  public isIade: boolean = false;

  public isNurseProfile: boolean = false;

  public isEffectifGarde: boolean = false;

  public isMultiHospital: boolean = false;

  public formatedFromDate: string | undefined;

  public formatedToDate: string | undefined;

  public isEffectiveNeedsLoading: boolean = true;

  public isFormationsAndParamedicRulesLoading: boolean = true;

  public formations: Formation[] | undefined;

  public paramedicRules: Paramedical[] | undefined;

  public hospitalNames: HospitalNames;

  public accessOption: AccessOptions | undefined;

  public isNurse: boolean = false;

  public showEffectiveNeedsByReasonsMaps = false;

  public gotEffectif: boolean = false;

  constructor(
    private readonly userService: UserService,
    private readonly profileService: ProfileService,
    private readonly errorService: ErrorService,
    private readonly calendarRequestService: CalendarRequestService,
    private readonly paramedicalService: ParamedicalService,
    private readonly router: Router,
    private destroyRef: DestroyRef,
    private subscriptionManager: SubscriptionManager,
    private instanceIdService: InstanceIdService
  ) {
    this.instanceId = this.instanceIdService.generateId();
    this.subscriptionManager.registerComponent(this.instanceId, this.destroyRef);
    this.hospitalNames = this.userService.getHospitalNames();
    this.accessOption = this.userService.getAccessOptionsOfPage(this.router.url);
  }

  async ngOnInit() {
    this.isNurse = await this.userService.isProfileInNurses(this.cr.profile);
    this.isCRCampaign = this.cr.requestCampaign ? true : false;
    this.showEffectiveNeedsByReasonsMaps = this.cr?.showEffectiveNeedsByReasonsMaps;
    this.isMultiHospital = this.userService.isCurrentUserHasMultipleHospitalsSelected();
    if (!this.isCRCampaign && !this.showEffectiveNeedsByReasonsMaps) {
      this.isEffectifGarde = this.cr.reason && this.cr.reason.isGarde;
      await this.setPosition();
      this.getNumberOfRequests(this.cr.hoveredDate.substring(0, 10));
      this.getFormationsAndParamedicRules();
      this.getEffectif(this.cr.hoveredDate, this.cr.hoveredDate);
      this.formatedFromDate = this.formatDate(this.cr.fromDate.substring(0, 10));
      this.formatedToDate = this.formatDate(this.cr.toDate.substring(0, 10));
    } else if (this.showEffectiveNeedsByReasonsMaps) {
      await this.setPosition();
      this.needsEffectifs = [
        {
          effectifs: this.cr.effectifs,
          needs: this.cr.needs,
          difference: this.cr.effectifs - this.cr.needs,
        },
      ];
    } else {
      this.formatedToDate = this.formatDate(this.cr.date.substring(0, 10));
    }
  }

  ngOnChanges() {
    this.isCRCampaign = this.cr.requestCampaign ? true : false;

    if (!this.isCRCampaign) {
      this.formatedFromDate = this.formatDate(this.cr.fromDate.substring(0, 10));
      this.formatedToDate = this.formatDate(this.cr.toDate.substring(0, 10));
    } else {
      this.formatedToDate = this.formatDate(this.cr.date.substring(0, 10));
    }
  }

  getFormations(): Observable<Formation[]> {
    return this.paramedicalService.getFormations();
  }

  getParamedicRules(): Observable<Paramedical[]> {
    return this.paramedicalService.getParamedicals();
  }

  getFormationsAndParamedicRules(): void {
    this.isFormationsAndParamedicRulesLoading = true;

    const getFormationsObservable = this.getFormations();
    const getParamedicRulesObservable = this.getParamedicRules();

    this.subscriptionManager
      .createRequest(
        forkJoin([getFormationsObservable, getParamedicRulesObservable]),
        'getFormationsAndParamedicRules',
        this.instanceId
      )
      .subscribe({
        next: (data: [Formation[], Paramedical[]]) => {
          this.formations = data[0];
          this.paramedicRules = data[1];
          this.isFormationsAndParamedicRulesLoading = false;
        },

        error: (error: any) => {
          this.errorService.handleError(error);
        },
      });
  }

  private get isConsultaion(): boolean {
    return this.cr.reason && this.cr.reason.title === CONSULTATION;
  }

  private get isBloc(): boolean {
    return this.cr.reason && this.cr.reason.title === BLOC;
  }

  async setPosition() {
    const position = this.cr.profile.position;

    if (position == ANESTHETIST) {
      this.position = ANESTHETIST;
      this.isAnesthesiste = true;
    } else if (await this.userService.isProfileInIades(this.cr.profile)) {
      this.position = IADE[0];
      this.isIade = true;
    } else {
      this.position = NURSE;
      this.isNurseProfile = true;
    }
  }

  formatDate(str: string) {
    const arr = str.split('-');
    return arr[2] + '-' + arr[1] + '-' + arr[0];
  }

  getEffectif(startDate: string, endDate: string) {
    if (!this.gotEffectif) {
      this.subscriptionManager
        .createRequest(
          this.profileService.getAverages({
            startDate: startDate.substring(0, 10),
            endDate: endDate.substring(0, 10),
            isGarde: this.isEffectifGarde,
            hospitalId: this.cr.hospital._id,
            effectifType: this.isAnesthesiste || this.isIade ? ANESTHETIST : NURSE,
            requestedDocuments: {
              effectif: true,
              nurseNeeds: this.isNurse,
              anesthNeeds: this.isAnesthesiste || this.isIade,
            },
          }),
          'getAverages',
          this.instanceId
        )
        .subscribe({
          next: async (result: any) => {
            this.gotEffectif = true;
            this.effectif = result.effectif ? sortByDate(result.effectif) : [];
            this.originalEffective = result.effectif ? sortByDate(result.effectif) : [];
            this.needs = result.nurseNeeds ? sortByDate(result.nurseNeeds) : [];
            this.originalNeeds = result.nurseNeeds ? sortByDate(result.nurseNeeds) : [];
            this.isEffectiveNeedsLoading = false;

            if (this.isAnesthesiste || this.isIade) {
              await this.setNeedsEffectifs(
                result.anesthNeeds,
                this.formatEffectif(result.effectif, result.anesthNeeds)
              );
            } else {
              this.fixEffectif();
              this.generateDifferences();
            }
          },
          error: (error: any) => this.errorService.handleError(error),
        });
    }
  }

  fixEffectif() {
    const effectifArray: any[] = [];
    for (const needItem of this.needs) {
      const eItem = this.effectif.find((effectifItem: any) => effectifItem.date === needItem.date);
      const item: any = {};
      item.morningNurseEffectif = this.getValueOrZero(eItem && eItem.morningNurseEffectif);
      item.afternoonNurseEffectif = this.getValueOrZero(eItem && eItem.afternoonNurseEffectif);
      item.nurseEffectifDay = this.min(item.morningNurseEffectif, item.afternoonNurseEffectif);
      item.anesthEffectif = this.getValueOrZero(eItem && eItem.anesth);
      item.anesth = this.getValueOrZero(eItem && eItem.anesth);
      item.iade = this.getValueOrZero(eItem && eItem.iade);
      item.date = this.getValueOrZero(needItem.date);
      item.day = this.getValueOrZero(needItem.day + 1);
      effectifArray.push(item);
    }
    this.effectif = effectifArray;
    this.needs[0].DayNurseNeed = this.max(this.needs[0].morningNurseNeed, this.needs[0].afternoonNurseNeed);
  }

  getValueOrZero(value: number) {
    return value ? value : 0;
  }

  generateDifferences(): void {
    const morningDiffs: number[] = [];
    const afternoonDiffs: number[] = [];

    for (const i in this.needs) {
      if (this.effectif[i]) {
        morningDiffs.push(this.effectif[i].morningNurseEffectif - this.needs[i].morningNurseNeed);
        afternoonDiffs.push(this.effectif[i].afternoonNurseEffectif - this.needs[i].afternoonNurseNeed);
        this.dayDiffs.push(this.effectif[0].nurseEffectifDay - this.needs[0].DayNurseNeed);
      }
    }
  }

  formatEffectif(effectifs: any, needs: any) {
    const effectifArray: any[] = [];
    for (const needItem of needs) {
      const eItem = effectifs.find(
        (effectifItem: any) => effectifItem.date === moment(needItem.date).format('YYYY-MM-DD')
      );
      effectifArray.push(eItem);
    }
    return effectifArray;
  }

  async setNeedsEffectifs(needs: any[], effectifs: any[]) {
    const hospital = getFirstHospitalSelectedData(
      this.userService.getCurrentUser(),
      this.userService.getSelectedHospitals()
    );
    const hospitalType = hospital?.type;

    const accessOptionsHaveIades = await this.userService.doesAccessOptionHaveIades(this.accessOption);
    const accessOptionsHaveAnesths = await this.userService.doesAccessOptionHaveAnesthesistes(this.accessOption);

    if (hospitalType === 'hopital privé') {
      if (
        accessOptionsHaveAnesths &&
        accessOptionsHaveIades &&
        !(this.accessOption?.seniority?.length === 1 && this.accessOption?.seniority?.[0] === 'Interne')
      ) {
        this.needsEffectifs = needs.map((_: any, i: any) => {
          const effectif = effectifs[i] ? effectifs[i].totalBloc + effectifs[i].totalConsultation : 0;
          const need = needs[i] ? needs[i].blocNeeds + needs[i].consultationNeeds : 0;
          return this.getEffectifNeeds(effectif, need);
        });
      } else if (this.accessOption?.seniority?.length === 1 && this.accessOption?.seniority?.[0] === 'Interne') {
        this.needsEffectifs = needs.map((_: any, i: any) => {
          const effectif = effectifs[i].detailsBloc ? effectifs[i].totalBloc + effectifs[i].totalConsultation : 0;
          const need = needs[i] ? needs[i].blocNeeds + needs[i].consultationNeeds : 0;
          return this.getEffectifNeeds(effectif, need);
        });
      } else if (!accessOptionsHaveAnesths && accessOptionsHaveIades) {
        this.needsEffectifs = needs.map((_: any, i: any) => {
          const effectif = effectifs[i] ? effectifs[i].iade : 0;
          const need = needs[i] ? needs[i].iadeNeeds : 0;
          return this.getEffectifNeeds(effectif, need);
        });
      } else {
        this.needsEffectifs = needs.map((_: any, i: any) => {
          const effectif = effectifs[i] ? effectifs[i].totalBloc + effectifs[i].totalConsultation : 0;
          const need = needs[i] ? needs[i].blocNeeds + needs[i].consultationNeeds : 0;
          return this.getEffectifNeeds(effectif, need);
        });
      }
    } else {
      if (this.isIade) {
        this.needsEffectifs = needs.map((_: any, i: any) => {
          const effectif = effectifs[i] ? effectifs[i].iade : 0;
          const need = needs[i] ? needs[i].iadeNeeds : 0;
          return this.getEffectifNeeds(effectif, need);
        });
      } else if (
        this.isBloc &&
        this.accessOption?.seniority?.length === 1 &&
        this.accessOption?.seniority?.[0] === 'Interne'
      ) {
        this.needsEffectifs = needs.map((_: any, i: any) => {
          const effectif = effectifs[i].detailsBloc ? effectifs[i].totalBloc + effectifs[i].totalConsultation : 0;
          const need = needs[i] ? needs[i].blocNeeds + needs[i].consultationNeeds : 0;
          return this.getEffectifNeeds(effectif, need);
        });
      } else if (
        this.isBloc &&
        !(this.accessOption?.seniority?.length === 1 && this.accessOption?.seniority?.[0] === 'Interne')
      ) {
        this.needsEffectifs = needs.map((_: any, i: any) => {
          const effectif = effectifs[i] ? effectifs[i].anesthBloc : 0;
          const need = needs[i] ? needs[i].blocAnesthNeeds : 0;
          return this.getEffectifNeeds(effectif, need);
        });
      } else if (this.isConsultaion) {
        this.needsEffectifs = needs.map((_: any, i: any) => {
          const effectif = effectifs[i] ? effectifs[i].totalConsultation : 0;
          const need = needs[i] ? needs[i].consultationNeeds : 0;
          return this.getEffectifNeeds(effectif, need);
        });
      } else {
        this.needsEffectifs = needs.map((_: any, i: any) => {
          const effectif = effectifs[i] ? effectifs[i].anesth : 0;
          const need = needs[i] ? needs[i].blocAnesthNeeds + needs[i].consultationNeeds : 0;
          return this.getEffectifNeeds(effectif, need);
        });
      }
    }
  }

  getEffectifNeeds(effectif: any, need: any) {
    return {
      effectifs: effectif,
      needs: need,
      difference: effectif - need,
    };
  }

  max(number1: number, number2: number) {
    if (number1 > number2) {
      return number1;
    } else {
      return number2;
    }
  }

  min(number1: number, number2: number) {
    if (number1 < number2) {
      return number1;
    } else {
      return number2;
    }
  }

  getNumberOfRequests(date: string) {
    if (this.position) {
      this.subscriptionManager
        .createRequest(
          this.calendarRequestService.countActiveRequests(this.position, date, date),
          'getNumberOfRequests',
          this.instanceId
        )
        .subscribe({
          next: (res: any) => {
            this.requestsCounter = res;
          },
          error: (err: any) => this.errorService.handleError(err),
        });
    }
  }
}
